// Admin panel: Bitcoin quizzes.
//
// Admins (city + superadmin) create one quiz per (city, period) — UNIQUE
// constraint enforces "at most one per month per city". The same panel is
// the moderator's console on the night: register walk-in teams, type
// scores (one total, or per-round columns), settle ties, and publish the
// board live between rounds.
//
// City scoping: a non-superadmin only ever sees and edits their own
// cities. The backend enforces it too — this just avoids showing buttons
// that would 403.

import { useEffect, useMemo, useState, type FC } from "react";
import {
  fetchQuizzes, fetchQuizDetail, adminSaveQuiz, adminDeleteQuiz,
  adminSetQuizScores, adminDeleteQuizTeam, adminAddQuizTeam,
  adminRemoveQuizTeamFromQuiz, adminPublishQuizResults,
  adminListQuizQuestions, adminSaveQuizQuestions,
  type Quiz, type QuizDetail, type QuizQuestion, type AdminUser,
  type QuizDivision, type AdminScoreEntry,
} from "../../api";
import { isApiError } from "../../lib/http";
import { CITIES, cityName } from "../../data/cities";
import { formatDateTime } from "../../lib/fmt";

/** Cities this admin may pick from. Empty `admin.cities` means
 *  unrestricted (superadmin or a legacy account), so offer everything. */
function allowedCities(admin: AdminUser) {
  const pickable = CITIES.filter((c) => !c.nationwide);
  if (!admin.cities || admin.cities.length === 0) return pickable;
  return pickable.filter((c) => admin.cities.includes(c.slug));
}

const MONTHS_FULL_CZ = [
  "Leden", "Únor", "Březen", "Duben", "Květen", "Červen",
  "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec",
];
function periodLabel(p: string): string {
  const m = p.match(/^(\d{4})-(\d{2})$/);
  if (!m) return p;
  const idx = Math.max(0, Math.min(11, Number(m[2]) - 1));
  return `${MONTHS_FULL_CZ[idx]} ${m[1]}`;
}

function defaultPeriod(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function periodFromIso(iso: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export const QuizzesView: FC<{ token: string; admin: AdminUser }> = ({ token, admin }) => {
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [reloadKey, setReloadKey] = useState(0);
  const [adding, setAdding] = useState(false);
  const [scoreFor, setScoreFor] = useState<string | null>(null);
  const [questionsFor, setQuestionsFor] = useState<string | null>(null);
  const [filterCity, setFilterCity] = useState<string>(admin.city ?? "");
  const [busyPublish, setBusyPublish] = useState<string | null>(null);

  const cities = useMemo(() => allowedCities(admin), [admin]);
  const scoped = cities.length < CITIES.filter((c) => !c.nationwide).length;

  useEffect(() => {
    let mounted = true;
    fetchQuizzes(filterCity || undefined).then((q) => {
      if (!mounted) return;
      // Belt and braces: the list endpoint is public and unscoped, so
      // filter to what this admin can actually act on.
      setQuizzes(scoped ? q.filter((x) => cities.some((c) => c.slug === x.city)) : q);
    });
    return () => { mounted = false; };
  }, [reloadKey, filterCity, cities, scoped]);

  const remove = async (id: string) => {
    if (!window.confirm("Smazat kvíz? Smažou se i všechna přihlášení a skóre.")) return;
    const r = await adminDeleteQuiz(token, id);
    if (!r.ok) { alert(r.error ?? "Smazání selhalo."); return; }
    setReloadKey((k) => k + 1);
  };

  const togglePublish = async (q: Quiz) => {
    const next = !q.resultsPublic;
    if (next && !window.confirm(
      "Zveřejnit průběžné výsledky? Uvidí je každý v appce. Až budou mít body všechny týmy, odejde push do města.",
    )) return;
    setBusyPublish(q.id);
    const r = await adminPublishQuizResults(token, q.id, next);
    setBusyPublish(null);
    if (!r.ok) { alert(r.error ?? "Selhalo."); return; }
    setReloadKey((k) => k + 1);
  };

  return (
    <div>
      <div className="row-actions" style={{ marginBottom: "0.9rem" }}>
        <button className="btn btn-primary" onClick={() => setAdding(true)}>+ Nový kvíz</button>
        <button className="btn btn-ghost btn-sm" onClick={() => setReloadKey((k) => k + 1)}>Obnovit</button>
        <select
          value={filterCity}
          onChange={(e) => setFilterCity(e.target.value)}
          style={{ marginLeft: "auto" }}
        >
          <option value="">{scoped ? "Moje města" : "Všechna města"}</option>
          {cities.map((c) => (
            <option key={c.slug} value={c.slug}>{c.name}</option>
          ))}
        </select>
      </div>

      {scoped && (
        <p className="small muted" style={{ margin: "0 0 0.9rem" }}>
          Spravuješ {cities.map((c) => c.name).join(", ")}. Kvízy z jiných měst ti panel neukáže a server je odmítne.
        </p>
      )}

      {adding && (
        <QuizForm
          token={token}
          admin={admin}
          onSaved={() => { setAdding(false); setReloadKey((k) => k + 1); }}
          onCancel={() => setAdding(false)}
        />
      )}

      {quizzes.length === 0 ? (
        <div className="empty-state"><p>Zatím žádné kvízy.</p></div>
      ) : (
        quizzes.map((q) => (
          <div key={q.id} className="card admin-quiz-card">
            <div className="flex-row space-between" style={{ alignItems: "flex-start", gap: "0.75rem" }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="small muted" style={{ textTransform: "uppercase", letterSpacing: "0.05em", fontFamily: "var(--mono)" }}>
                  {periodLabel(q.period)} · {cityName(q.city)}
                </div>
                <div style={{ fontWeight: 700, marginTop: 4 }}>{q.title}</div>
                <div className="small muted" style={{ marginTop: 4 }}>
                  {formatDateTime(q.startsAt)} · {q.location}
                </div>
                <div className="rsvp-counts">
                  <span className="pill"><span className="n">{q.teamCount}</span> týmů</span>
                  {q.rounds > 0 && <span className="pill">{q.rounds} kol</span>}
                  {q.scored && <span className="pill going">vyhodnoceno</span>}
                  {q.resultsPublic && <span className="pill going">výsledky veřejné</span>}
                </div>
              </div>
              <div className="row-actions" style={{ marginTop: 0, gap: "0.4rem", flexDirection: "column", alignItems: "flex-end" }}>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => { setScoreFor(scoreFor === q.id ? null : q.id); setQuestionsFor(null); }}
                >
                  {scoreFor === q.id ? "Zavřít" : "Zadat skóre"}
                </button>
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={() => togglePublish(q)}
                  disabled={busyPublish === q.id}
                  title="Pustit průběžnou výsledkovku lidem do appky"
                >
                  {q.resultsPublic ? "Skrýt výsledky" : "Zveřejnit výsledky"}
                </button>
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={() => { setQuestionsFor(questionsFor === q.id ? null : q.id); setScoreFor(null); }}
                >
                  {questionsFor === q.id ? "Zavřít otázky" : "Otázky"}
                </button>
                <button className="btn btn-sm btn-danger" onClick={() => remove(q.id)}>Smazat</button>
              </div>
            </div>
            {scoreFor === q.id && (
              <ScorePanel
                token={token}
                quiz={q}
                isSuperadmin={admin.role === "superadmin"}
                onSaved={() => setReloadKey((k) => k + 1)}
              />
            )}
            {questionsFor === q.id && (
              <QuestionsPanel token={token} quizId={q.id} />
            )}
          </div>
        ))
      )}
    </div>
  );
};

const QuizForm: FC<{
  token: string;
  admin: AdminUser;
  onSaved: () => void;
  onCancel: () => void;
}> = ({ token, admin, onSaved, onCancel }) => {
  const cities = useMemo(() => allowedCities(admin), [admin]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [city, setCity] = useState<string>(admin.city ?? cities[0]?.slug ?? "praha");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [location, setLocation] = useState("");
  const [url, setUrl] = useState("");
  const [rounds, setRounds] = useState("0");
  const [period, setPeriod] = useState(defaultPeriod());
  const [periodAuto, setPeriodAuto] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // Auto-derive period from startsAt unless admin overrode it manually.
  useEffect(() => {
    if (!periodAuto || !startsAt) return;
    const p = periodFromIso(startsAt);
    if (p) setPeriod(p);
  }, [startsAt, periodAuto]);

  const save = async () => {
    if (!title.trim() || !startsAt || !endsAt || !location.trim() || !city || !period) {
      setErr("Vyplň název, město, období, datum a místo.");
      return;
    }
    const roundsNum = Number(rounds || "0");
    if (!Number.isInteger(roundsNum) || roundsNum < 0 || roundsNum > 20) {
      setErr("Počet kol 0–20 (0 = jedno celkové skóre).");
      return;
    }
    setBusy(true); setErr("");
    const r = await adminSaveQuiz(token, {
      title: title.trim(),
      description: description.trim(),
      city,
      period,
      location: location.trim(),
      ...(url.trim() ? { url: url.trim() } : {}),
      rounds: roundsNum,
      startsAt: new Date(startsAt).toISOString(),
      endsAt: new Date(endsAt).toISOString(),
    });
    setBusy(false);
    if (isApiError(r)) { setErr(r.error); return; }
    onSaved();
  };

  return (
    <div className="card" style={{ marginBottom: "1rem" }}>
      <h3>Nový kvíz</h3>
      <div className="form">
        <div className="field">
          <label>Název *</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        </div>
        <div className="field">
          <label>Popis</label>
          <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="flex-row" style={{ gap: "0.7rem" }}>
          <div className="field" style={{ flex: 1 }}>
            <label>Město *</label>
            <select value={city} onChange={(e) => setCity(e.target.value)}>
              {cities.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label>Období (YYYY-MM) *</label>
            <input
              value={period}
              onChange={(e) => { setPeriod(e.target.value); setPeriodAuto(false); }}
              placeholder="2026-04"
            />
            {periodAuto && <p className="small muted" style={{ margin: "4px 0 0" }}>Doplňuje se z datumu začátku.</p>}
          </div>
        </div>
        <div className="field">
          <label>Místo (hospoda / lokace) *</label>
          <input value={location} onChange={(e) => setLocation(e.target.value)} />
        </div>
        <div className="flex-row" style={{ gap: "0.7rem" }}>
          <div className="field" style={{ flex: 1 }}>
            <label>Odkaz (volitelné)</label>
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" />
          </div>
          <div className="field" style={{ width: 150 }}>
            <label>Počet kol</label>
            <input
              type="number"
              min={0}
              max={20}
              value={rounds}
              onChange={(e) => setRounds(e.target.value)}
            />
            <p className="small muted" style={{ margin: "4px 0 0" }}>
              0 = jedno celkové skóre. Jinak zadáváš body po kolech a součet dopočítá server.
            </p>
          </div>
        </div>
        <div className="flex-row" style={{ gap: "0.7rem" }}>
          <div className="field" style={{ flex: 1 }}>
            <label>Začátek *</label>
            <input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label>Konec *</label>
            <input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
          </div>
        </div>
        {err && <p className="error">{err}</p>}
        <div className="row-actions">
          <button className="btn btn-primary" onClick={save} disabled={busy}>
            {busy ? "Ukládám…" : "Uložit kvíz"}
          </button>
          <button className="btn btn-ghost" onClick={onCancel}>Zrušit</button>
        </div>
      </div>
    </div>
  );
};

// The moderator's console for one quiz: register teams that walked in,
// type points (one total, or one column per round), settle ties, and see
// the standings update as you type.
const ScorePanel: FC<{
  token: string;
  quiz: Quiz;
  isSuperadmin: boolean;
  onSaved: () => void;
}> = ({ token, quiz, isSuperadmin, onSaved }) => {
  const quizId = quiz.id;
  const roundCount = quiz.rounds;
  const [detail, setDetail] = useState<QuizDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [scores, setScores] = useState<Record<string, string>>({});
  const [tiebreaks, setTiebreaks] = useState<Record<string, string>>({});
  const [rounds, setRounds] = useState<Record<string, string[]>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    // Admin token so scores come back even before the board is public.
    fetchQuizDetail(quizId, token).then((d) => {
      if (!mounted) return;
      setDetail(d);
      const s: Record<string, string> = {};
      const tb: Record<string, string> = {};
      const rd: Record<string, string[]> = {};
      for (const t of d?.teams ?? []) {
        if (t.score !== null) s[t.id] = String(t.score);
        if (t.tiebreak) tb[t.id] = String(t.tiebreak);
        rd[t.id] = Array.from({ length: roundCount }, (_, i) =>
          t.rounds[i] === undefined || t.rounds[i] === null ? "" : String(t.rounds[i]),
        );
      }
      setScores(s); setTiebreaks(tb); setRounds(rd);
      setLoading(false);
    });
    return () => { mounted = false; };
  }, [quizId, token, roundCount, reloadKey]);

  /** What a team currently totals, from rounds or the single field. */
  const totalFor = (teamId: string): number | null => {
    if (roundCount > 0) {
      const cells = rounds[teamId] ?? [];
      const filled = cells.filter((c) => c.trim() !== "");
      if (filled.length === 0) return null;
      return filled.reduce((acc, c) => acc + (Number(c) || 0), 0);
    }
    const raw = scores[teamId];
    if (!raw || raw.trim() === "") return null;
    return Number(raw);
  };

  const setRoundCell = (teamId: string, idx: number, value: string) => {
    const cells = [...(rounds[teamId] ?? Array.from({ length: roundCount }, () => ""))];
    cells[idx] = value;
    setRounds({ ...rounds, [teamId]: cells });
  };

  const submit = async () => {
    setBusy(true); setErr("");
    const payload: AdminScoreEntry[] = [];
    for (const t of detail?.teams ?? []) {
      const tbRaw = tiebreaks[t.id] ?? "";
      const tb = tbRaw.trim() === "" ? 0 : Number(tbRaw);
      if (!Number.isInteger(tb)) { setBusy(false); setErr("Tie-break musí být celé číslo."); return; }

      if (roundCount > 0) {
        const cells = rounds[t.id] ?? [];
        if (cells.every((c) => c.trim() === "")) continue;
        const parsed: Array<number | null> = [];
        for (const c of cells) {
          if (c.trim() === "") { parsed.push(null); continue; }
          const n = Number(c);
          if (!Number.isInteger(n)) { setBusy(false); setErr("Body za kolo musí být celé číslo."); return; }
          parsed.push(n);
        }
        payload.push({ teamId: t.id, rounds: parsed, tiebreak: tb });
      } else {
        const raw = scores[t.id] ?? "";
        if (raw.trim() === "") continue;
        const n = Number(raw);
        if (!Number.isInteger(n)) { setBusy(false); setErr("Skóre musí být celé číslo."); return; }
        payload.push({ teamId: t.id, score: n, tiebreak: tb });
      }
    }
    if (payload.length === 0) { setBusy(false); setErr("Žádné skóre nezadáno."); return; }
    const r = await adminSetQuizScores(token, quizId, payload);
    setBusy(false);
    if (!r.ok) { setErr(r.error ?? "Selhalo uložení."); return; }
    setReloadKey((k) => k + 1);
    onSaved();
  };

  // Drop the team from this quiz only — the everyday "didn't show up".
  const dropFromQuiz = async (teamId: string, teamName: string) => {
    if (!window.confirm(`Odebrat „${teamName}" z tohohle kvízu? Tým i jeho výsledky odjinud zůstanou.`)) return;
    const r = await adminRemoveQuizTeamFromQuiz(token, quizId, teamId);
    if (!r.ok) { alert(r.error ?? "Nepovedlo se."); return; }
    setReloadKey((k) => k + 1);
    onSaved();
  };

  // Nuke the team across every city. Server refuses if the team has
  // played outside this admin's cities.
  const deleteTeamEverywhere = async (teamId: string, teamName: string) => {
    if (!window.confirm(
      `Smazat tým „${teamName}" úplně? Zmizí ze všech kvízů včetně jiných měst, i s badgi. Tohle nejde vrátit.`,
    )) return;
    const r = await adminDeleteQuizTeam(token, teamId);
    if (!r.ok) { alert(r.error ?? "Smazání selhalo."); return; }
    setReloadKey((k) => k + 1);
    onSaved();
  };

  if (loading) return <div className="loading" style={{ padding: "1rem 0" }}>Načítám…</div>;
  if (!detail) return <p className="error small">Kvíz nenalezen.</p>;

  // Live standings from what's currently typed, per division, so the
  // moderator can read the room's placing before saving.
  const standings = (["novacci", "pokrocili"] as QuizDivision[]).map((division) => {
    const rows = detail.teams
      .filter((t) => t.division === division)
      .map((t) => ({ id: t.id, name: t.name, total: totalFor(t.id), tb: Number(tiebreaks[t.id] ?? 0) || 0 }))
      .filter((t) => t.total !== null)
      .sort((a, b) => (b.total! - a.total!) || (b.tb - a.tb) || a.name.localeCompare(b.name, "cs"));
    return { division, rows };
  }).filter((s) => s.rows.length > 0);

  return (
    <div style={{ marginTop: "0.8rem", paddingTop: "0.8rem", borderTop: "1px solid var(--hairline)" }}>
      <h4 style={{ margin: "0 0 0.5rem" }}>
        Týmy ({detail.teams.length})
        {(() => {
          // Sum confirmed headcounts; ignore null-headcount teams so "to plan
          // capacity for" matches what captains actually pledged.
          const confirmed = detail.teams.filter((t) => t.headcount !== null);
          const confirmedSum = confirmed.reduce((acc, t) => acc + (t.headcount ?? 0), 0);
          if (confirmed.length === 0) return null;
          return (
            <span className="small muted" style={{ marginLeft: 8 }}>
              · potvrzená účast: {confirmedSum} lidí ({confirmed.length} z {detail.teams.length} týmů)
            </span>
          );
        })()}
      </h4>

      <AddTeamRow
        token={token}
        quizId={quizId}
        onAdded={() => { setReloadKey((k) => k + 1); onSaved(); }}
      />

      {detail.teams.length === 0 ? (
        <p className="small muted">Zatím žádný tým. Přidej ho řádkem výš — jde to i po startu kvízu.</p>
      ) : (
        <>
          {detail.teams.map((t) => {
            const total = totalFor(t.id);
            return (
              <div key={t.id} className="admin-quiz-score-row" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                <div style={{ minWidth: 160, flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{t.name}</div>
                  <div className="small muted">
                    {t.createdByOwnerId === null
                      ? <span className="pill">zapsán organizátorem</span>
                      : <>{t.memberCount} {t.memberCount === 1 ? "člen" : "členů"}</>}
                    {" · "}
                    <span className={`pill ${t.division === "pokrocili" ? "going" : ""}`}>
                      {t.division === "pokrocili" ? "Pokročilí" : "Nováčci"}
                    </span>
                    {t.headcount !== null && (
                      <> · <span className="pill">Přijde {t.headcount}</span></>
                    )}
                  </div>
                </div>

                <div style={{ display: "flex", gap: "0.3rem", alignItems: "center", flexWrap: "wrap" }}>
                  {roundCount > 0 ? (
                    <>
                      {Array.from({ length: roundCount }, (_, i) => (
                        <input
                          key={i}
                          type="number"
                          inputMode="numeric"
                          title={`${i + 1}. kolo`}
                          placeholder={`${i + 1}.`}
                          style={{ width: 58 }}
                          value={rounds[t.id]?.[i] ?? ""}
                          onChange={(e) => setRoundCell(t.id, i, e.target.value)}
                        />
                      ))}
                      <span className="pill" style={{ minWidth: 54, textAlign: "center" }} title="Součet kol">
                        Σ {total ?? "—"}
                      </span>
                    </>
                  ) : (
                    <input
                      type="number"
                      inputMode="numeric"
                      value={scores[t.id] ?? ""}
                      onChange={(e) => setScores({ ...scores, [t.id]: e.target.value })}
                      placeholder="—"
                    />
                  )}
                  <input
                    type="number"
                    inputMode="numeric"
                    title="Tie-break — rozhoduje jen při shodě bodů"
                    placeholder="TB"
                    style={{ width: 58 }}
                    value={tiebreaks[t.id] ?? ""}
                    onChange={(e) => setTiebreaks({ ...tiebreaks, [t.id]: e.target.value })}
                  />
                  <button
                    className="btn btn-sm btn-ghost"
                    title="Odebrat z tohohle kvízu (tým zůstane)"
                    onClick={() => dropFromQuiz(t.id, t.name)}
                  >
                    ×
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    title={isSuperadmin
                      ? "Smazat tým úplně, ve všech městech"
                      : "Smazat tým úplně — jen pokud hrál výhradně ve tvých městech"}
                    onClick={() => deleteTeamEverywhere(t.id, t.name)}
                  >
                    🗑
                  </button>
                </div>
              </div>
            );
          })}

          {standings.length > 0 && (
            <div className="small muted" style={{ marginTop: "0.6rem" }}>
              {standings.map((s) => (
                <div key={s.division}>
                  <strong>{s.division === "pokrocili" ? "Pokročilí" : "Nováčci"}:</strong>{" "}
                  {s.rows.map((r, i) => `${i + 1}. ${r.name} (${r.total})`).join(" · ")}
                </div>
              ))}
            </div>
          )}

          {err && <p className="error small">{err}</p>}
          <div className="row-actions">
            <button className="btn btn-primary" onClick={submit} disabled={busy}>
              {busy ? "Ukládám…" : "Uložit skóre"}
            </button>
            <span className="small muted">
              {quiz.resultsVisible
                ? "Výsledky jsou veřejné — uložení je hned vidí i lidi v appce."
                : "Výsledky jsou zatím skryté. Zveřejníš je tlačítkem na kartě kvízu."}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

// Register a team on the night. A name collision comes back with the
// existing team's id, so the moderator gets a one-click "attach it"
// instead of having to invent a different name at the microphone.
const AddTeamRow: FC<{
  token: string;
  quizId: string;
  onAdded: () => void;
}> = ({ token, quizId, onAdded }) => {
  const [name, setName] = useState("");
  const [division, setDivision] = useState<QuizDivision>("novacci");
  const [headcount, setHeadcount] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [collision, setCollision] = useState<{ id: string; name: string } | null>(null);

  const reset = () => { setName(""); setHeadcount(""); setErr(""); setCollision(null); };

  const submit = async (attachId?: string) => {
    if (!attachId && name.trim().length < 2) { setErr("Název aspoň 2 znaky."); return; }
    const hc = headcount.trim() === "" ? null : Number(headcount);
    if (hc !== null && (!Number.isInteger(hc) || hc < 1 || hc > 50)) { setErr("Počet lidí 1–50."); return; }
    setBusy(true); setErr(""); setCollision(null);
    const r = await adminAddQuizTeam(token, quizId, {
      ...(attachId ? { teamId: attachId } : { name: name.trim() }),
      division,
      headcount: hc,
    });
    setBusy(false);
    if (!r.ok) {
      setErr(r.error);
      if (r.existingTeamId) setCollision({ id: r.existingTeamId, name: r.existingTeamName ?? name.trim() });
      return;
    }
    reset();
    onAdded();
  };

  return (
    <div className="card" style={{ padding: "0.6rem", marginBottom: "0.7rem" }}>
      <div className="flex-row" style={{ gap: "0.4rem", alignItems: "center", flexWrap: "wrap" }}>
        <input
          value={name}
          onChange={(e) => { setName(e.target.value); setErr(""); setCollision(null); }}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="Tým, co přišel na místě…"
          maxLength={60}
          style={{ flex: 1, minWidth: 160 }}
        />
        <select value={division} onChange={(e) => setDivision(e.target.value as QuizDivision)}>
          <option value="novacci">Nováčci</option>
          <option value="pokrocili">Pokročilí</option>
        </select>
        <input
          type="number"
          min={1}
          max={50}
          value={headcount}
          onChange={(e) => setHeadcount(e.target.value)}
          placeholder="lidí"
          style={{ width: 78 }}
        />
        <button className="btn btn-sm btn-primary" onClick={() => submit()} disabled={busy}>
          {busy ? "…" : "+ Přidat tým"}
        </button>
      </div>
      {err && (
        <p className="error small" style={{ margin: "0.4rem 0 0" }}>
          {err}
          {collision && (
            <>
              {" "}
              <button className="btn btn-sm btn-ghost" onClick={() => submit(collision.id)} disabled={busy}>
                Přihlásit existující „{collision.name}"
              </button>
            </>
          )}
        </p>
      )}
    </div>
  );
};

// Replace-all editor for the question archive. Admin sees the whole list
// regardless of quiz state; the public reveal is gated server-side on
// quiz.ends_at < now (see GET /api/quizzes/:id).
const QuestionsPanel: FC<{ token: string; quizId: string }> = ({ token, quizId }) => {
  const [items, setItems] = useState<Array<Pick<QuizQuestion, "question" | "answer" | "sourceUrl"> & { id?: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    adminListQuizQuestions(token, quizId).then((qs) => {
      if (!mounted) return;
      setItems(qs.length > 0 ? qs : [{ question: "", answer: "", sourceUrl: null }]);
      setLoading(false);
    });
    return () => { mounted = false; };
  }, [token, quizId]);

  const setField = (i: number, patch: Partial<typeof items[number]>) =>
    setItems(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));

  const add = () => setItems([...items, { question: "", answer: "", sourceUrl: null }]);
  const remove = (i: number) => setItems(items.filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true); setErr("");
    const cleaned = items
      .map((it) => ({
        ...(it.id ? { id: it.id } : {}),
        question: (it.question ?? "").trim(),
        answer: (it.answer ?? "").trim(),
        sourceUrl: it.sourceUrl?.trim() || null,
      }))
      .filter((it) => it.question.length > 0 && it.answer.length > 0);
    const r = await adminSaveQuizQuestions(token, quizId, cleaned);
    setBusy(false);
    if (!r.ok) { setErr(r.error ?? "Selhalo uložení."); return; }
  };

  if (loading) return <div className="loading" style={{ padding: "1rem 0" }}>Načítám otázky…</div>;

  return (
    <div style={{ marginTop: "0.8rem", paddingTop: "0.8rem", borderTop: "1px solid var(--hairline)" }}>
      <h4 style={{ margin: "0 0 0.5rem" }}>Archiv otázek</h4>
      <p className="small muted">
        Otázky jsou veřejné až po skončení kvízu (po <code>endsAt</code>). Prázdné řádky se ukládáním zahodí.
      </p>
      {items.map((it, i) => (
        <div key={i} className="card" style={{ marginBottom: "0.6rem", padding: "0.7rem" }}>
          <div className="flex-row" style={{ justifyContent: "space-between", alignItems: "center" }}>
            <strong className="small">Otázka {i + 1}</strong>
            <button className="btn btn-sm btn-ghost" onClick={() => remove(i)} title="Smazat">×</button>
          </div>
          <div className="field">
            <label className="small">Otázka</label>
            <textarea
              rows={2}
              value={it.question}
              onChange={(e) => setField(i, { question: e.target.value })}
            />
          </div>
          <div className="field">
            <label className="small">Správná odpověď</label>
            <textarea
              rows={2}
              value={it.answer}
              onChange={(e) => setField(i, { answer: e.target.value })}
            />
          </div>
          <div className="field">
            <label className="small">Zdroj / odkaz (volitelné)</label>
            <input
              value={it.sourceUrl ?? ""}
              onChange={(e) => setField(i, { sourceUrl: e.target.value })}
              placeholder="https://…"
            />
          </div>
        </div>
      ))}
      <div className="row-actions">
        <button className="btn btn-ghost btn-sm" onClick={add}>+ Přidat otázku</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>
          {busy ? "Ukládám…" : "Uložit otázky"}
        </button>
      </div>
      {err && <p className="error small">{err}</p>}
    </div>
  );
};
